import { Link } from 'react-router-dom';
import { useTheme } from '@/hooks/useTheme';

const CATEGORIES = [
  {
    key: 'Track Pants',
    icon: 'ri-run-line',
    emoji: '🏃',
    label: 'Track Pants',
    sub: 'Gym & Street',
    gradient: 'from-orange-500 to-red-500',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    text: 'text-orange-700',
    iconColor: 'text-orange-500',
    image: 'https://readdy.ai/api/search-image?query=Premium%20black%20baggy%20track%20pants%20streetwear%20flat%20lay%20product%20photography%2C%20minimal%20clean%20background%2C%20bold%20urban%20fashion%20style%2C%20high%20contrast%20lighting&width=200&height=260&seq=chip_tp&orientation=portrait',
  },
  {
    key: 'T-Shirts',
    icon: 'ri-t-shirt-line',
    emoji: '👕',
    label: 'T-Shirts',
    sub: 'All Styles',
    gradient: 'from-rose-500 to-pink-500',
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    text: 'text-rose-700',
    iconColor: 'text-rose-500',
    image: 'https://readdy.ai/api/search-image?query=Premium%20white%20crew%20neck%20t-shirt%20flat%20lay%20product%20photography%2C%20clean%20minimal%20cotton%20fabric%2C%20soft%20studio%20lighting%20on%20light%20grey%20background%2C%20high-end%20fashion%20e-commerce&width=200&height=260&seq=chip_ts&orientation=portrait',
  },
  {
    key: 'Jeans',
    icon: 'ri-layout-bottom-line',
    emoji: '👖',
    label: 'Jeans',
    sub: 'Slim & More',
    gradient: 'from-amber-500 to-yellow-500',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-700',
    iconColor: 'text-amber-500',
    image: 'https://readdy.ai/api/search-image?query=Premium%20slim%20fit%20dark%20indigo%20wash%20denim%20jeans%20folded%20product%20photography%2C%20clean%20modern%20denim%20design%2C%20professional%20studio%20lighting%20on%20soft%20neutral%20background&width=200&height=260&seq=chip_jn&orientation=portrait',
  },
  {
    key: 'Hoodies',
    icon: 'ri-shirt-line',
    emoji: '🧥',
    label: 'Hoodies',
    sub: 'Cozy Fits',
    gradient: 'from-violet-500 to-indigo-500',
    bg: 'bg-violet-50',
    border: 'border-violet-200',
    text: 'text-violet-700',
    iconColor: 'text-violet-500',
    image: 'https://readdy.ai/api/search-image?query=Premium%20oversized%20grey%20pullover%20hoodie%20flat%20lay%20product%20photography%2C%20thick%20fleece%20kangaroo%20pocket%20design%2C%20soft%20cozy%20fabric%2C%20professional%20studio%20lighting%20on%20clean%20light%20background&width=200&height=260&seq=chip_hd&orientation=portrait',
  },
  {
    key: 'Shorts',
    icon: 'ri-scissors-cut-line',
    emoji: '🩳',
    label: 'Shorts',
    sub: 'Sport & Casual',
    gradient: 'from-emerald-500 to-teal-500',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    text: 'text-emerald-700',
    iconColor: 'text-emerald-500',
    image: 'https://readdy.ai/api/search-image?query=Premium%20navy%20blue%20athletic%20sport%20shorts%20product%20photography%20flat%20lay%2C%20lightweight%20moisture-wicking%20fabric%2C%20professional%20studio%20lighting%20on%20clean%20white%20surface%2C%20high-end%20activewear&width=200&height=260&seq=chip_sh&orientation=portrait',
  },
];

export default function CategoryChips() {
  const { colors } = useTheme();
  
  // Generate dynamic colors for each category based on theme
  const getCategoryStyle = (index: number) => {
    const styles = [
      { from: colors.primary, to: colors.secondary, bg: `${colors.primary}15`, border: `${colors.primary}30`, text: colors.primary },
      { from: colors.secondary, to: colors.accent, bg: `${colors.secondary}15`, border: `${colors.secondary}30`, text: colors.secondary },
      { from: colors.accent, to: colors.primary, bg: `${colors.accent}15`, border: `${colors.accent}30`, text: colors.accent },
      { from: colors.primary, to: colors.secondary, bg: `${colors.primary}15`, border: `${colors.primary}30`, text: colors.primary },
      { from: colors.secondary, to: colors.accent, bg: `${colors.secondary}15`, border: `${colors.secondary}30`, text: colors.secondary },
    ];
    return styles[index % styles.length];
  };

  return (
    <section className="py-8" style={{ backgroundColor: colors.bg }}>
      <div className="max-w-2xl mx-auto px-4">

        {/* Section Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">Browse</p>
            <h2 className="text-gray-900 dark:text-white font-black text-xl leading-tight">
              Shop by <span 
                className="text-transparent bg-clip-text"
                style={{ backgroundImage: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` }}
              >Category</span>
            </h2>
          </div>
          <Link
            to="/category?cat=T-Shirts"
            className="flex items-center gap-1 text-xs font-bold text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white transition-colors"
          >
            All <i className="ri-arrow-right-s-line"></i>
          </Link>
        </div>

        {/* Horizontal Scrollable Chips */}
        <div
          className="flex gap-3 overflow-x-auto pb-2"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {CATEGORIES.map((cat, index) => {
            const style = getCategoryStyle(index);
            return (
              <Link
                key={cat.key}
                to={`/category?cat=${encodeURIComponent(cat.key)}`}
                className="shrink-0 flex flex-col items-center gap-2 cursor-pointer group"
                style={{ width: 80 }}
              >
                {/* Image circle */}
                <div
                  className="relative w-20 h-20 rounded-2xl overflow-hidden border-2 transition-all duration-300 group-hover:scale-105 group-active:scale-95"
                  style={{ 
                    background: '#f9fafb',
                    borderColor: style.border
                  }}
                >
                  <img
                    src={cat.image}
                    alt={cat.label}
                    className="w-full h-full object-cover object-top"
                  />
                  {/* Gradient overlay */}
                  <div 
                    className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-300"
                    style={{ background: `linear-gradient(135deg, ${style.from}, ${style.to})` }}
                  ></div>
                  {/* Icon badge bottom-right */}
                  <div 
                    className="absolute bottom-1 right-1 w-5 h-5 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: 'white' }}
                  >
                    <i className={`${cat.icon} text-[10px]`} style={{ color: style.text }}></i>
                  </div>
                </div>

                {/* Label */}
                <div className="text-center">
                  <p className="text-gray-900 dark:text-white font-bold text-[11px] leading-tight">{cat.label}</p>
                  <p className="text-gray-400 dark:text-gray-500 text-[9px] font-medium">{cat.sub}</p>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Bottom CTA strip */}
        <div className="mt-5 flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
          {CATEGORIES.map((cat, index) => {
            const style = getCategoryStyle(index);
            return (
              <Link
                key={cat.key}
                to={`/category?cat=${encodeURIComponent(cat.key)}`}
                className="shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[11px] font-bold border transition-all hover:scale-105 active:scale-95 cursor-pointer whitespace-nowrap"
                style={{ 
                  backgroundColor: style.bg,
                  borderColor: style.border,
                  color: style.text
                }}
              >
                <i className={`${cat.icon} text-xs`}></i>
                {cat.label}
              </Link>
            );
          })}
        </div>

      </div>
    </section>
  );
}
